import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useLocation, useHistory } from 'react-router-dom';
import './InspirationCanvas.css';
import ApiService from '../../api/ApiService';
import LogService from '../../api/LogService';
import { useAuth } from '../../components/auth/AuthContext';
import CanvasWorkspaceSidebar from '../../components/CanvasWorkspaceSidebar/CanvasWorkspaceSidebar';
import juchachaLogo from '../../assets/juchacha-super-factory-logo.png';
import { toast } from 'react-toastify';

const apiService = new ApiService();

const CAMPAIGN_STATUS_TEXT = {
  upcoming: '即将开始',
  active: '报名进行中',
  full: '本期名额已满',
  paused: '活动已暂停',
  ended: '活动已结束'
};

const formatCampaignTime = (value) => {
  if (!value) return '—';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return value;
  return parsed.toLocaleString('zh-CN', {
    timeZone: 'Asia/Shanghai',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false
  });
};

const getProjectDateValue = (project) => (
  project?.updated_at
  || project?.created_at
  || project?.last_accessed_at
  || project?.modified_at
  || ''
);

const getProjectTimestamp = (project) => {
  const value = getProjectDateValue(project);
  const timestamp = value ? new Date(value).getTime() : Number.NaN;
  return Number.isFinite(timestamp) ? timestamp : 0;
};

const sortProjectsNewestFirst = (projects) => [...projects].sort((left, right) => {
  const timeDifference = getProjectTimestamp(right) - getProjectTimestamp(left);
  if (timeDifference !== 0) return timeDifference;
  return String(right?.uuid || right?.id || '').localeCompare(
    String(left?.uuid || left?.id || ''),
    undefined,
    { numeric: true }
  );
});

const formatProjectDate = (value) => {
  if (!value) return '时间未知';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '时间未知';
  return date.toLocaleDateString('zh-CN', {
    timeZone: 'Asia/Shanghai',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit'
  });
};

const getProjectStableKey = (project) => String(
  project?.uuid || project?.id || project?.name || ''
);

export const getProjectCoverUrl = (project) => {
  const coverUrl = [
    project?.thumbnail_url,
    project?.thumbnailUrl,
    project?.cover_url,
    project?.coverUrl
  ].find((value) => typeof value === 'string' && value.trim());
  return coverUrl ? coverUrl.trim() : '';
};

export const hasProjectCover = (project) => Boolean(getProjectCoverUrl(project));

const parseImageRatio = (value) => {
  if (typeof value === 'number' && Number.isFinite(value) && value > 0) {
    return value;
  }

  if (typeof value !== 'string') return null;
  const normalized = value.trim();
  if (!normalized) return null;

  const ratioParts = normalized.split(/[:/x]/i).map(Number);
  if (ratioParts.length === 2 && ratioParts.every((part) => Number.isFinite(part) && part > 0)) {
    return ratioParts[0] / ratioParts[1];
  }

  const numericRatio = Number(normalized);
  return Number.isFinite(numericRatio) && numericRatio > 0 ? numericRatio : null;
};

const getProjectImageRatio = (project, knownRatios = {}) => {
  const knownRatio = parseImageRatio(knownRatios[getProjectStableKey(project)]);
  if (knownRatio) return knownRatio;

  const dimensions = project?.thumbnail || project?.cover || project?.dimensions || {};
  const width = Number(
    project?.thumbnail_width
    || project?.cover_width
    || project?.width
    || dimensions.width
  );
  const height = Number(
    project?.thumbnail_height
    || project?.cover_height
    || project?.height
    || dimensions.height
  );
  if (width > 0 && height > 0) return width / height;

  return parseImageRatio(
    project?.thumbnail_aspect_ratio
    || project?.cover_aspect_ratio
    || project?.aspect_ratio
  ) || 4 / 3;
};

export const distributeProjectsIntoMasonryColumns = (projects, columnCount, knownRatios = {}) => {
  const safeColumnCount = Math.max(1, Math.floor(Number(columnCount) || 1));
  const columns = Array.from({ length: safeColumnCount }, () => []);
  const estimatedHeights = Array(safeColumnCount).fill(0);

  (Array.isArray(projects) ? projects : []).forEach((project) => {
    let shortestColumnIndex = 0;
    for (let index = 1; index < estimatedHeights.length; index += 1) {
      if (estimatedHeights[index] < estimatedHeights[shortestColumnIndex]) {
        shortestColumnIndex = index;
      }
    }

    columns[shortestColumnIndex].push(project);
    const imageRatio = getProjectImageRatio(project, knownRatios);
    estimatedHeights[shortestColumnIndex] += (1 / imageRatio) + 0.16;
  });

  return columns;
};

const MASONRY_SKELETON_RATIOS = [
  0.82, 1.14, 0.72, 1.28, 0.96, 0.78, 1.2, 0.9
];

const MasonrySkeletonGrid = ({ columns, compact = false }) => {
  const skeletonCount = Math.max(columns * 2, 6);
  const skeletons = Array.from({ length: skeletonCount }, (_, index) => ({
    id: `skeleton-${index}`,
    ratio: compact ? 16 / 9 : MASONRY_SKELETON_RATIOS[index % MASONRY_SKELETON_RATIOS.length]
  }));

  return (
    <div
      className="InspirationCanvas-skeleton-grid"
      style={{ '--InspirationCanvas-skeleton-columns': columns }}
      role="status"
      aria-label="正在加载作品"
    >
      {skeletons.map((skeleton) => (
        <div className="InspirationCanvas-skeleton-item" key={skeleton.id}>
          <div
            className="InspirationCanvas-skeleton-card"
            style={{ aspectRatio: skeleton.ratio }}
            aria-hidden="true"
          >
            <span className="InspirationCanvas-skeleton-shimmer" />
          </div>
        </div>
      ))}
    </div>
  );
};

const getGeneratedImageUrl = (image) => {
  if (typeof image === 'string') return image;
  return image?.url || image?.image_url || image?.imageUrl || null;
};

export const extractProjectGeneratedImages = (snapshot) => {
  const nodes = Array.isArray(snapshot?.nodes) ? snapshot.nodes : [];
  const seenUrls = new Set();

  return nodes.flatMap((node) => {
    const generatedImages = Array.isArray(node?.data?.generatedImages)
      ? node.data.generatedImages
      : [];
    const nodeLabel = node?.data?.modelId || node?.data?.taskType || '生成结果';

    return generatedImages.reduce((images, image, index) => {
      const url = getGeneratedImageUrl(image);
      if (
        typeof url !== 'string'
        || !url
        || url.startsWith('data:')
        || url.startsWith('blob:')
        || seenUrls.has(url)
      ) {
        return images;
      }

      seenUrls.add(url);
      images.push({
        id: `${node?.id || 'node'}-${index}-${url}`,
        url,
        label: nodeLabel,
        index: index + 1
      });
      return images;
    }, []);
  });
};

const InspirationCanvas = () => {
  const location = useLocation();
  const history = useHistory();
  const { user: userInfo, openLogin } = useAuth();
  
  // 从 URL 查询参数读取初始 tab
  const getInitialTab = () => {
    const searchParams = new URLSearchParams(location.search);
    const tabParam = searchParams.get('tab');
    return tabParam === 'mine' ? 'mine' : 'discovery';
  };
  
  const [activeTab, setActiveTab] = useState(getInitialTab); // 'discovery' | 'mine'
  
  // 当 tab 改变时，清除 URL 中的 tab 参数（避免刷新时保留）
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    if (searchParams.has('tab')) {
      searchParams.delete('tab');
      const newSearch = searchParams.toString();
      history.replace({
        pathname: location.pathname,
        search: newSearch ? `?${newSearch}` : ''
      });
    }
  }, []);
  
  // Discovery projects state
  const [discoveryProjects, setDiscoveryProjects] = useState([]);
  const [discoveryPage, setDiscoveryPage] = useState(1);
  const [discoveryHasMore, setDiscoveryHasMore] = useState(true);
  const [discoveryLoading, setDiscoveryLoading] = useState(true);

  // My projects state
  const [mineProjects, setMineProjects] = useState([]);
  const [minePage, setMinePage] = useState(1);
  const [mineHasMore, setMineHasMore] = useState(true);
  const [mineLoading, setMineLoading] = useState(false);

  // Masonry columns state
  const [numColumns, setNumColumns] = useState(4);
  const [coverRatios, setCoverRatios] = useState({});
  const [loadedCoverKeys, setLoadedCoverKeys] = useState(() => new Set());
  const [brokenCoverKeys, setBrokenCoverKeys] = useState(() => new Set());

  const [error, setError] = useState(null);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [upgradeMessage, setUpgradeMessage] = useState('');
  const [pointsCampaign, setPointsCampaign] = useState(null);
  const [campaignLoading, setCampaignLoading] = useState(true);
  const [campaignClaiming, setCampaignClaiming] = useState(false);
  const defaultUpgradeMessage = '普通会员最多只能创建1个项目（包括克隆），请升级会员等级';

  const observer = useRef();
  const loadingRef = useRef({ discovery: false, mine: false });
  // 移除 lastProjectElementRef，改用在每个列表末尾添加一个哨兵元素来触发加载

  // 监听窗口大小变化调整列数
  useEffect(() => {
    const handleResize = () => {
      const width = window.innerWidth;
      if (width >= 1920) setNumColumns(6);
      else if (width >= 1440) setNumColumns(5);
      else if (width >= 1024) setNumColumns(4);
      else if (width >= 640) setNumColumns(3);
      else setNumColumns(2);
    };
    
    handleResize(); // Initial check
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const extractErrorMessage = (err, defaultMsg = '操作失败') => {
    if (!err) return defaultMsg;
    if (typeof err === 'string') return err;

    if (err.data) {
      if (typeof err.data === 'string') {
        try {
          const parsed = JSON.parse(err.data);
          if (parsed) {
            if (Array.isArray(parsed.detail)) {
              return parsed.detail.join('; ');
            }
            if (parsed.detail) {
              return parsed.detail;
            }
            if (parsed.message) {
              return parsed.message;
            }
            if (parsed.msg) {
              return parsed.msg;
            }
          }
        } catch (parseError) {
          if (err.data.trim()) {
            return err.data;
          }
        }
      } else if (typeof err.data === 'object' && err.data !== null) {
        if (Array.isArray(err.data.detail)) {
          return err.data.detail.join('; ');
        }
        if (err.data.detail && typeof err.data.detail === 'object') {
          return err.data.detail.message || err.data.detail.reason || defaultMsg;
        }
        return err.data.detail || err.data.message || err.data.msg || defaultMsg;
      }
    }

    if (err.message && typeof err.message === 'string') {
      return err.message;
    }

    return defaultMsg;
  };

  const loadPointsCampaign = useCallback(async () => {
    setCampaignLoading(true);
    try {
      const response = await apiService.getActivePointsCampaign('juchacha_canvas');
      setPointsCampaign(response?.data || null);
    } catch (campaignError) {
      console.warn('活动信息加载失败:', campaignError);
      setPointsCampaign(null);
    } finally {
      setCampaignLoading(false);
    }
  }, []);

  useEffect(() => {
    loadPointsCampaign();
  }, [loadPointsCampaign, userInfo?.token]);
  
  // 创建/编辑项目对话框状态
  const [showDialog, setShowDialog] = useState(false);
  const [editingProject, setEditingProject] = useState(null);
  const [projectForm, setProjectForm] = useState({
    name: '',
    description: '',
    is_public: false
  });
  
  // 删除确认对话框状态
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [deletingProject, setDeletingProject] = useState(null);
  const [deleteError, setDeleteError] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [openProjectMenuId, setOpenProjectMenuId] = useState(null);
  const [coverProject, setCoverProject] = useState(null);
  const [coverImages, setCoverImages] = useState([]);
  const [selectedCoverUrl, setSelectedCoverUrl] = useState(null);
  const [coverLoading, setCoverLoading] = useState(false);
  const [coverError, setCoverError] = useState(null);
  const [coverSaving, setCoverSaving] = useState(false);
  const coverInputRef = useRef(null);

  // 初始化公开灵感内容
  useEffect(() => {
    // 记录页面访问日志
    LogService.logPageView('灵感画布', {
      pageCategory: '创作工具',
      pageType: '项目管理页面',
      pageFeatures: ['项目列表', '创建项目', '推荐案例']
    });
    
    // 初始加载 Discovery
    loadProjects('discovery', 1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 切换 Tab 时加载数据（如果是第一次切到这个Tab）
  useEffect(() => {
    if (activeTab === 'mine' && mineProjects.length === 0 && userInfo) {
      loadProjects('mine', 1);
    }
  }, [activeTab, userInfo]);

  // 加载项目列表
  const loadProjects = async (type, page) => {
    const limit = 40;
    const skip = (page - 1) * limit;

    if (type === 'mine' && !userInfo?.token) {
      setMineLoading(false);
      return;
    }
    if (loadingRef.current[type]) return;
    loadingRef.current[type] = true;
    
    try {
      setError(null);
      
      if (type === 'discovery') {
        setDiscoveryLoading(true);
        const data = await apiService.getPublicDrawProjects(skip, limit);
        const receivedProjects = Array.isArray(data?.projects) ? data.projects : [];
        const newProjects = receivedProjects.filter(hasProjectCover);
        
        if (page === 1) {
          setDiscoveryProjects(newProjects);
          setCoverRatios({});
          setLoadedCoverKeys(new Set());
          setBrokenCoverKeys(new Set());
        } else {
          setDiscoveryProjects(prev => {
            // 去重，防止key重复
            const existingIds = new Set(prev.map(getProjectStableKey));
            const uniqueNewProjects = newProjects.filter(p => !existingIds.has(getProjectStableKey(p)));
            return [...prev, ...uniqueNewProjects];
          });
        }
        
        // 过滤无封面项目后仍按接口返回数量判断，避免因为过滤导致漏掉下一页。
        setDiscoveryHasMore(receivedProjects.length === limit);
        setDiscoveryPage(page);
      } else {
        // Mine
        setMineLoading(true);
        const data = await apiService.getMyDrawProjects(userInfo.token, skip, limit);
        const newProjects = Array.isArray(data?.projects) ? data.projects : [];
        
        if (page === 1) {
          setMineProjects(newProjects);
        } else {
          setMineProjects(prev => {
             const existingIds = new Set(prev.map(getProjectStableKey));
             const uniqueNewProjects = newProjects.filter(p => !existingIds.has(getProjectStableKey(p)));
             return [...prev, ...uniqueNewProjects];
          });
        }
        
        setMineHasMore(newProjects.length === limit);
        setMinePage(page);
      }
    } catch (err) {
      const errorMsg = extractErrorMessage(err, '加载项目列表失败');
      setError(errorMsg);
      console.error(`加载${type}项目失败:`, err);
    } finally {
      if (type === 'discovery') setDiscoveryLoading(false);
      else setMineLoading(false);
      loadingRef.current[type] = false;
    }
  };

  // 刷新当前 Tab 数据
  const refreshCurrentTab = async () => {
    if (activeTab === 'discovery') {
      setDiscoveryPage(1);
      setDiscoveryHasMore(true);
      setDiscoveryProjects([]);
      setCoverRatios({});
      setLoadedCoverKeys(new Set());
      setBrokenCoverKeys(new Set());
      await loadProjects('discovery', 1);
    } else {
      setMinePage(1);
      setMineHasMore(true);
      setMineProjects([]);
      setLoadedCoverKeys(new Set());
      setBrokenCoverKeys(new Set());
      await loadProjects('mine', 1);
    }
  };

  // 生成随机项目名称
  const generateRandomProjectName = () => {
    const adjectives = ['创意', '智能', '未来', '梦想', '灵感', '神奇', '精彩', '炫酷', '极致', '超凡'];
    const nouns = ['画布', '项目', '创作', '空间', '作品', '设计', '方案'];
    const randomAdjective = adjectives[Math.floor(Math.random() * adjectives.length)];
    const randomNoun = nouns[Math.floor(Math.random() * nouns.length)];
    const timestamp = new Date().getTime().toString().slice(-4);
    return `${randomAdjective}${randomNoun}_${timestamp}`;
  };

  const getProjectIdentifier = (project) => {
    if (!project) {
      return null;
    }
    return project.uuid || project.id || null;
  };

  const sortedMineProjects = useMemo(() => sortProjectsNewestFirst(mineProjects), [mineProjects]);

  const handleProjectCoverLoad = useCallback((project, event) => {
    const coverKey = `${getProjectStableKey(project)}::${getProjectCoverUrl(project)}`;
    setLoadedCoverKeys((current) => {
      if (current.has(coverKey)) return current;
      const next = new Set(current);
      next.add(coverKey);
      return next;
    });

    if (activeTab !== 'discovery') return;
    const naturalWidth = Number(event.currentTarget?.naturalWidth);
    const naturalHeight = Number(event.currentTarget?.naturalHeight);
    if (!naturalWidth || !naturalHeight) return;

    const projectKey = getProjectStableKey(project);
    const ratio = naturalWidth / naturalHeight;
    setCoverRatios((current) => (
      current[projectKey] === ratio
        ? current
        : { ...current, [projectKey]: ratio }
    ));
  }, [activeTab]);

  const handleProjectCoverError = useCallback((project) => {
    const projectKey = getProjectStableKey(project);
    const coverKey = `${projectKey}::${getProjectCoverUrl(project)}`;
    setLoadedCoverKeys((current) => {
      if (!current.has(coverKey)) return current;
      const next = new Set(current);
      next.delete(coverKey);
      return next;
    });
    setBrokenCoverKeys((current) => {
      if (current.has(projectKey)) return current;
      const next = new Set(current);
      next.add(projectKey);
      return next;
    });
    // 公开灵感不保留失效封面，避免图片错误状态撑出空白卡片。
    setDiscoveryProjects((current) => current.filter((item) => getProjectStableKey(item) !== projectKey));
  }, []);

  useEffect(() => {
    if (!openProjectMenuId) return undefined;

    const closeMenu = (event) => {
      if (!event.target?.closest?.('.InspirationCanvas-project-menu')) {
        setOpenProjectMenuId(null);
      }
    };
    const closeMenuOnEscape = (event) => {
      if (event.key === 'Escape') setOpenProjectMenuId(null);
    };

    document.addEventListener('mousedown', closeMenu);
    document.addEventListener('keydown', closeMenuOnEscape);
    return () => {
      document.removeEventListener('mousedown', closeMenu);
      document.removeEventListener('keydown', closeMenuOnEscape);
    };
  }, [openProjectMenuId]);

  useEffect(() => {
    if (!coverProject) return undefined;

    const closeCoverPickerOnEscape = (event) => {
      if (event.key === 'Escape' && !coverSaving) {
        setCoverProject(null);
      }
    };

    document.addEventListener('keydown', closeCoverPickerOnEscape);
    return () => document.removeEventListener('keydown', closeCoverPickerOnEscape);
  }, [coverProject, coverSaving]);

  // 快速创建项目并打开
  const handleCreateClick = async () => {
    // 记录按钮点击日志
    LogService.logButtonClick('立即创建', 'create-project-button', {
      actionType: '创建新项目',
      pageName: '灵感画布'
    });
    
    try {
      if (!userInfo || !userInfo.token) {
        openLogin();
        return;
      }
      
      const randomName = generateRandomProjectName();
      const newProject = await apiService.createDrawProject(userInfo.token, randomName, '', false);
      
      // 记录项目创建成功事件
      LogService.logCustomEvent('project_created', {
        projectName: randomName,
        actionResult: 'success',
        pageName: '灵感画布'
      });
      
      toast.success('项目创建成功');
      // 刷新我的项目列表
      if (activeTab === 'mine') {
        await refreshCurrentTab();
      } else {
        // 如果在发现页创建，切换到我的页面并刷新
        setActiveTab('mine');
        // useEffect 会触发加载，这里不需要手动 refresh
      }
      
      // 跳转到画布编辑器
      const projectIdentifier = getProjectIdentifier(newProject);
      if (!projectIdentifier) {
        toast.error('新项目缺少唯一标识，无法打开');
        return;
      }

      // 创建的项目属于"我的"，添加来源参数以便返回时回到我的卡片
      history.push(`/canvas-editor/${projectIdentifier}?from=my-canvas`);
      console.log('创建的项目:', newProject);
    } catch (err) {
      const errorMsg = extractErrorMessage(err, '创建项目失败');
      const normalizedMessage = (errorMsg || '').trim();
      
      // 记录项目创建失败事件
      LogService.logCustomEvent('project_create_failed', {
        errorMessage: normalizedMessage,
        actionResult: 'error',
        pageName: '灵感画布'
      });
      
      if (normalizedMessage.includes('升级会员') || normalizedMessage.includes('普通会员')) {
        const cleanedMessage = normalizedMessage.replace(/^创建项目失败[:：]?\s*/, '');
        setUpgradeMessage(cleanedMessage || defaultUpgradeMessage);
        setShowUpgradeModal(true);
      } else {
        toast.error(normalizedMessage || '创建项目失败');
      }
      console.error('创建项目失败:', err);
    }
  };

  // 打开编辑对话框
  const handleEditClick = (project) => {
    setOpenProjectMenuId(null);
    // 记录编辑按钮点击日志
    LogService.logButtonClick('编辑项目', 'edit-project-button', {
      projectName: project.name,
      projectId: getProjectIdentifier(project),
      actionType: '打开编辑对话框',
      pageName: '灵感画布'
    });
    
    setEditingProject(project);
    setProjectForm({
      name: project.name,
      description: project.description || '',
      is_public: project.is_public
    });
    setShowDialog(true);
  };

  const handleToggleVisibility = async (project) => {
    setOpenProjectMenuId(null);
    if (!userInfo?.token) {
      openLogin();
      return;
    }

    const projectIdentifier = getProjectIdentifier(project);
    if (!projectIdentifier) {
      toast.error('项目缺少唯一标识，无法更新公开状态');
      return;
    }

    const nextIsPublic = !project.is_public;
    try {
      await apiService.updateDrawProject(userInfo.token, projectIdentifier, { is_public: nextIsPublic });
      setMineProjects((current) => current.map((item) => (
        getProjectIdentifier(item) === projectIdentifier
          ? { ...item, is_public: nextIsPublic }
          : item
      )));
      toast.success(nextIsPublic ? '项目已设为公开' : '项目已设为私有');
    } catch (err) {
      toast.error(extractErrorMessage(err, '公开状态更新失败'));
    }
  };

  const closeCoverPicker = (force = false) => {
    if (coverSaving && !force) return;
    setCoverProject(null);
    setCoverImages([]);
    setSelectedCoverUrl(null);
    setCoverError(null);
  };

  const handleChangeCoverClick = async (project) => {
    setOpenProjectMenuId(null);

    if (!userInfo?.token) {
      openLogin();
      return;
    }

    const projectIdentifier = getProjectIdentifier(project);
    if (!projectIdentifier) {
      toast.error('项目缺少唯一标识，无法更换封面');
      return;
    }

    setCoverProject(project);
    setCoverImages([]);
    setSelectedCoverUrl(null);
    setCoverError(null);
    setCoverLoading(true);

    try {
      const projectDetail = await apiService.getDrawProject(userInfo.token, projectIdentifier);
      let snapshot = projectDetail?.content;

      if (projectDetail?.canvas_url) {
        const canvasResponse = await apiService.loadDrawCanvas(userInfo.token, projectIdentifier);
        snapshot = canvasResponse?.snapshot || canvasResponse?.content || snapshot;
      }

      setCoverImages(extractProjectGeneratedImages(snapshot));
    } catch (err) {
      setCoverError(extractErrorMessage(err, '读取项目生成结果失败'));
    } finally {
      setCoverLoading(false);
    }
  };

  const handleSaveSelectedCover = async () => {
    if (!selectedCoverUrl || !coverProject || !userInfo?.token) return;

    const projectIdentifier = getProjectIdentifier(coverProject);
    if (!projectIdentifier) {
      toast.error('项目缺少唯一标识，无法更换封面');
      return;
    }

    setCoverSaving(true);
    try {
      await apiService.updateDrawProject(userInfo.token, projectIdentifier, {
        thumbnail_url: selectedCoverUrl
      });
      setMineProjects((current) => current.map((item) => (
        getProjectIdentifier(item) === projectIdentifier
          ? { ...item, thumbnail_url: selectedCoverUrl, updated_at: new Date().toISOString() }
          : item
      )));
      closeCoverPicker(true);
      toast.success('项目封面已更新');
    } catch (err) {
      toast.error(extractErrorMessage(err, '更换封面失败'));
    } finally {
      setCoverSaving(false);
    }
  };

  const handleCoverFileChange = async (event) => {
    const [file] = event.target.files || [];
    event.target.value = '';
    if (!file || !coverProject) return;
    if (!file.type.startsWith('image/')) {
      toast.error('请选择图片文件作为封面');
      return;
    }
    if (!userInfo?.token) {
      openLogin();
      return;
    }

    const projectIdentifier = getProjectIdentifier(coverProject);
    if (!projectIdentifier) {
      toast.error('项目缺少唯一标识，无法更换封面');
      return;
    }

    try {
      const uploadResult = await apiService.uploadImageToR2(file);
      const thumbnailUrl = uploadResult?.public_url;
      if (!thumbnailUrl) throw new Error('封面上传失败');
      await apiService.updateDrawProject(userInfo.token, projectIdentifier, {
        thumbnail_url: thumbnailUrl
      });
      setMineProjects((current) => current.map((item) => (
        getProjectIdentifier(item) === projectIdentifier
          ? { ...item, thumbnail_url: thumbnailUrl, updated_at: new Date().toISOString() }
          : item
      )));
      toast.success('项目封面已更新');
    } catch (err) {
      toast.error(extractErrorMessage(err, '更换封面失败'));
    } finally {
      setCoverProject(null);
    }
  };

  // 保存项目（更新）
  const handleSave = async () => {
    // 记录保存按钮点击日志
    LogService.logButtonClick('保存项目', 'save-project-button', {
      projectName: projectForm.name,
      isPublic: projectForm.is_public,
      actionType: '保存项目信息',
      pageName: '灵感画布'
    });
    
    try {
      if (!userInfo || !userInfo.token) {
        openLogin();
        return;
      }
      
      if (!projectForm.name.trim()) {
        toast.error('请输入项目名称');
        return;
      }

      if (editingProject) {
        const projectIdentifier = getProjectIdentifier(editingProject);
        if (!projectIdentifier) {
          toast.error('项目缺少唯一标识，无法保存');
          return;
        }

        await apiService.updateDrawProject(userInfo.token, projectIdentifier, projectForm);
        
        // 记录项目更新成功事件
        LogService.logCustomEvent('project_updated', {
          projectId: projectIdentifier,
          projectName: projectForm.name,
          isPublic: projectForm.is_public,
          actionResult: 'success',
          pageName: '灵感画布'
        });
        
        setShowDialog(false);
        await refreshCurrentTab();
        toast.success('项目已保存');
      }
    } catch (err) {
      const errorMsg = extractErrorMessage(err, '保存项目失败');
      
      // 记录项目更新失败事件
      LogService.logCustomEvent('project_update_failed', {
        errorMessage: errorMsg,
        actionResult: 'error',
        pageName: '灵感画布'
      });
      
      toast.error(errorMsg);
      console.error('保存项目失败:', err);
    }
  };

  // 打开删除确认对话框
  const handleDeleteClick = (project) => {
    setOpenProjectMenuId(null);
    // 记录删除按钮点击日志
    LogService.logButtonClick('删除项目', 'delete-project-button', {
      projectName: project.name,
      projectId: getProjectIdentifier(project),
      actionType: '打开删除确认对话框',
      pageName: '灵感画布'
    });
    
    setDeletingProject(project);
    setDeleteError(null);
    setShowDeleteDialog(true);
  };

  // 确认删除项目
  const handleConfirmDelete = async () => {
    if (!deletingProject) return;
    
    if (!userInfo || !userInfo.token) {
      openLogin();
      return;
    }

    // 记录确认删除按钮点击日志
    LogService.logButtonClick('确认删除', 'confirm-delete-button', {
      projectName: deletingProject.name,
      projectId: getProjectIdentifier(deletingProject),
      actionType: '确认删除项目',
      pageName: '灵感画布'
    });

    try {
      setIsDeleting(true);
      setDeleteError(null);
      const projectIdentifier = getProjectIdentifier(deletingProject);
      if (!projectIdentifier) {
        toast.error('项目缺少唯一标识，无法删除');
        return;
      }

      await apiService.deleteDrawProject(userInfo.token, projectIdentifier);
      
      // 记录项目删除成功事件
      LogService.logCustomEvent('project_deleted', {
        projectId: projectIdentifier,
        projectName: deletingProject.name,
        actionResult: 'success',
        pageName: '灵感画布'
      });
      
      setShowDeleteDialog(false);
      setDeletingProject(null);
      
      // 本地移除，不重新请求接口
      if (activeTab === 'mine') {
        setMineProjects(prev => prev.filter(p => getProjectIdentifier(p) !== projectIdentifier));
      }
      toast.success('项目已删除');
    } catch (err) {
      const errorMsg = extractErrorMessage(err, '删除项目失败');
      
      // 记录项目删除失败事件
      LogService.logCustomEvent('project_delete_failed', {
        projectId: getProjectIdentifier(deletingProject),
        errorMessage: errorMsg,
        actionResult: 'error',
        pageName: '灵感画布'
      });
      
      setDeleteError(errorMsg);
      console.error('删除项目失败:', err);
    } finally {
      setIsDeleting(false);
    }
  };

  // 取消删除
  const handleCancelDelete = () => {
    setShowDeleteDialog(false);
    setDeletingProject(null);
    setDeleteError(null);
  };

  // 打开项目
  const handleOpenProject = async (project) => {
    // 记录打开项目日志
    LogService.logButtonClick('打开项目', 'open-project-button', {
      projectName: project.name,
      projectId: getProjectIdentifier(project),
      isPublic: project.is_public || false,
      actionType: '打开画布编辑器',
      pageName: '灵感画布'
    });
    
    // 直接跳转到画布编辑器（不再自动克隆）
    const projectIdentifier = getProjectIdentifier(project);
    if (!projectIdentifier) {
      toast.error('项目缺少唯一标识，无法打开');
      return;
    }

    // 根据当前 tab 添加来源参数，以便返回时能回到正确的页面
    const fromParam = activeTab === 'mine' ? '?from=my-canvas' : '';
    history.push(`/canvas-editor/${projectIdentifier}${fromParam}`);
    console.log('打开项目:', project);
  };

  const closeUpgradeModal = () => {
    setShowUpgradeModal(false);
  };

  const handleGoToMembership = () => {
    // 记录去开通会员按钮点击日志
    LogService.logButtonClick('去开通会员', 'go-to-membership-button', {
      fromDialog: '升级会员提示',
      actionType: '跳转到会员充值页',
      pageName: '灵感画布'
    });
    
    setShowUpgradeModal(false);
    window.open('https://apiz.ai/#/recharge', '_blank', 'noopener,noreferrer');
  };

  const handleCampaignAction = async () => {
    if (!pointsCampaign) return;
    if (pointsCampaign.claimed) {
      await handleCreateClick();
      return;
    }
    if (!userInfo?.token) {
      openLogin('/');
      return;
    }
    if (pointsCampaign.computed_status !== 'active' || campaignClaiming) return;

    setCampaignClaiming(true);
    try {
      const response = await apiService.claimPointsCampaign(userInfo.token, pointsCampaign.id);
      const result = response?.data || {};
      if (result.campaign) setPointsCampaign(result.campaign);
      if (Number.isFinite(Number(result.points_balance))) {
        window.dispatchEvent(new CustomEvent('canvas:balance-updated', {
          detail: { pointsBalance: Number(result.points_balance) }
        }));
      }
      toast.success(
        result.newly_claimed
          ? `报名成功，已领取 ${result.reward_points} 积分`
          : '本期活动已经领取过了'
      );
    } catch (claimError) {
      toast.error(extractErrorMessage(claimError, '活动领取失败'));
      await loadPointsCampaign();
    } finally {
      setCampaignClaiming(false);
    }
  };

  // 按当前列的预计高度分配项目，避免固定轮转造成某一列持续偏长。
  const masonryColumns = useMemo(() => {
    if (activeTab === 'mine') {
      return [sortedMineProjects];
    }

    return distributeProjectsIntoMasonryColumns(discoveryProjects, numColumns, coverRatios);
  }, [activeTab, discoveryProjects, numColumns, coverRatios, sortedMineProjects]);

  // 哨兵元素 Ref
  const sentinelRef = useCallback(node => {
    const loading = activeTab === 'discovery' ? discoveryLoading : mineLoading;
    const hasMore = activeTab === 'discovery' ? discoveryHasMore : mineHasMore;

    if (loading) return;
    if (observer.current) observer.current.disconnect();
    observer.current = new IntersectionObserver(entries => {
      if (entries[0].isIntersecting && hasMore) {
        const nextPage = activeTab === 'discovery' ? discoveryPage + 1 : minePage + 1;
        loadProjects(activeTab, nextPage);
      }
    }, { rootMargin: '480px 0px' });
    if (node) observer.current.observe(node);
  }, [activeTab, discoveryLoading, mineLoading, discoveryHasMore, mineHasMore, discoveryPage, minePage]);

  const campaignStatus = pointsCampaign?.computed_status;
  const campaignButtonText = pointsCampaign?.claimed
    ? '已领取 · 开始创作'
    : !userInfo?.token && campaignStatus === 'active'
      ? '登录后报名领取'
      : campaignClaiming
        ? '正在领取…'
        : campaignStatus === 'active'
          ? `${pointsCampaign?.cta_text || '立即报名领取'} ${pointsCampaign?.reward_points || ''} 积分`
          : CAMPAIGN_STATUS_TEXT[campaignStatus] || '暂不可领取';
  const campaignButtonDisabled = campaignLoading || campaignClaiming || (
    !pointsCampaign?.claimed && campaignStatus !== 'active'
  );
  const activeLoading = activeTab === 'discovery' ? discoveryLoading : mineLoading;
  const activeProjectCount = activeTab === 'discovery' ? discoveryProjects.length : mineProjects.length;
  const isInitialProjectLoading = activeLoading && activeProjectCount === 0;

  return (
    <div className="InspirationCanvas-container">
      {/* 左侧导航栏 */}
      <CanvasWorkspaceSidebar
        activeKey={activeTab === 'mine' ? 'canvas' : 'inspiration'}
        onInspiration={() => setActiveTab('discovery')}
        onGenerate={() => history.push('/generate')}
        onAssets={() => history.push('/task-records')}
        onCanvas={() => setActiveTab('mine')}
      />

      {/* 右侧内容区 */}
      <div className={`InspirationCanvas-body${activeTab === 'mine' ? ' is-mine' : ''}`}>
        {/* 独立项目标题 */}
        <header className="InspirationCanvas-header">
          <div className="InspirationCanvas-header-inner">
            <div className="InspirationCanvas-heading">
              <h1>
                <span>欢迎进入</span>
                <img
                  className="InspirationCanvas-brand-logo"
                  src={juchachaLogo}
                  alt="剧查查超级工厂"
                />
              </h1>
            </div>
          </div>
        </header>
      
      {/* 内容区域 - JS 瀑布流布局 */}
      <main className="InspirationCanvas-main">
        {pointsCampaign && activeTab === 'discovery' && (
          <section className={`InspirationCanvas-campaign is-${campaignStatus}`} aria-label="限时积分活动">
            {pointsCampaign.cover_url && (
              <img className="InspirationCanvas-campaign-cover" src={pointsCampaign.cover_url} alt="" />
            )}
            <div className="InspirationCanvas-campaign-copy">
              <div className="InspirationCanvas-campaign-eyebrow">
                <span>{CAMPAIGN_STATUS_TEXT[campaignStatus] || '积分活动'}</span>
                <time>{formatCampaignTime(pointsCampaign.start_at)} — {formatCampaignTime(pointsCampaign.end_at)}</time>
              </div>
              <h2>{pointsCampaign.title}</h2>
              <p>{pointsCampaign.subtitle || '报名领取积分，开启你的灵感创作。'}</p>
              <div className="InspirationCanvas-campaign-progress" aria-label={`已领取 ${pointsCampaign.claimed_count} 人，共 ${pointsCampaign.participant_limit} 人`}>
                <span style={{ width: `${Math.min((pointsCampaign.claimed_count / pointsCampaign.participant_limit) * 100, 100)}%` }} />
              </div>
              <small>已领取 {pointsCampaign.claimed_count} 人 · 剩余 {pointsCampaign.remaining_count} 名</small>
            </div>
            <div className="InspirationCanvas-campaign-reward">
              <span>本期奖励</span>
              <strong>{pointsCampaign.reward_points}</strong>
              <small>积分</small>
              <button type="button" disabled={campaignButtonDisabled} onClick={handleCampaignAction}>
                {campaignButtonText}
              </button>
            </div>
          </section>
        )}

        <div className="InspirationCanvas-section-header">
          <div>
            <h2>{activeTab === 'mine' ? '我的项目' : '发现灵感'}</h2>
          </div>
          {activeTab === 'mine' && userInfo?.token ? (
            <button type="button" className="InspirationCanvas-create-project-button" onClick={handleCreateClick}>
              <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" aria-hidden="true">
                <path d="M12 5v14M5 12h14" />
              </svg>
              新建项目
            </button>
          ) : (
            <p>从公开作品中找到下一次创作的起点</p>
          )}
        </div>

        {error && (
          <div className="InspirationCanvas-error">
            <p>{error}</p>
            <button onClick={refreshCurrentTab}>重试</button>
          </div>
        )}

        {activeTab === 'mine' && !userInfo?.token ? (
          <section className="InspirationCanvas-login-prompt" aria-label="登录后使用灵感画布">
            <span className="InspirationCanvas-login-prompt-mark" aria-hidden="true">剧</span>
            <div>
              <h3>登录后创建你的灵感画布</h3>
              <p>公开作品可以直接浏览，登录后即可创建、保存并管理个人项目。</p>
            </div>
            <button type="button" onClick={() => openLogin()}>登录 / 注册</button>
          </section>
        ) : isInitialProjectLoading ? (
          <MasonrySkeletonGrid columns={numColumns} compact={activeTab === 'mine'} />
        ) : <div className={`InspirationCanvas-masonry-container${activeTab === 'mine' ? ' is-mine-projects' : ''}`}>
          {masonryColumns.map((columnItems, colIndex) => (
            <div key={colIndex} className="InspirationCanvas-masonry-column">
              {columnItems.map((item, itemIndex) => {
                const project = item;
                const projectIdentifier = getProjectIdentifier(project);
                const projectKey = getProjectStableKey(project);
                const projectMenuId = String(projectIdentifier || `${activeTab}-${project.name}-${colIndex}-${itemIndex}`);
                const projectDate = getProjectDateValue(project);
                const isProjectMenuOpen = openProjectMenuId === projectMenuId;
                const coverUrl = getProjectCoverUrl(project);
                const coverLoadKey = `${projectKey}::${coverUrl}`;
                const showCoverImage = Boolean(coverUrl) && !brokenCoverKeys.has(projectKey);
                const coverLoaded = loadedCoverKeys.has(coverLoadKey);

                return (
                  <div
                    key={projectIdentifier || projectKey || `${activeTab}-${project.name}-${colIndex}-${itemIndex}`}
                    className="InspirationCanvas-masonry-item"
                    role="button"
                    tabIndex="0"
                    aria-label={`打开项目${project.name}`}
                    onClick={() => handleOpenProject(project)}
                    onKeyDown={(event) => {
                      if (event.target !== event.currentTarget) return;
                      if (event.key === 'Enter' || event.key === ' ') {
                        event.preventDefault();
                        handleOpenProject(project);
                      }
                    }}
                  >
                    <div className={`InspirationCanvas-card-content${activeTab === 'mine' ? ' is-mine-project' : ''}`}>

                      <div className={`InspirationCanvas-card-thumbnail${showCoverImage && !coverLoaded ? ' is-loading' : ''}`}>
                        {showCoverImage ? (
                          <>
                            <img
                              src={coverUrl}
                              alt={project.name}
                              loading={itemIndex === 0 ? 'eager' : 'lazy'}
                              decoding="async"
                              onLoad={(event) => handleProjectCoverLoad(project, event)}
                              onError={() => handleProjectCoverError(project)}
                            />
                            {!coverLoaded && <span className="InspirationCanvas-card-image-skeleton" aria-hidden="true" />}
                          </>
                        ) : activeTab === 'mine' ? (
                          <div className="InspirationCanvas-card-placeholder">
                            <svg width="60" height="60" viewBox="0 0 60 60" fill="none">
                              <rect x="10" y="15" width="40" height="30" rx="2" stroke="currentColor" strokeWidth="2"/>
                              <circle cx="22" cy="25" r="3" fill="currentColor"/>
                              <path d="M10 35 L20 28 L30 35 L45 22 L50 27" stroke="currentColor" strokeWidth="2"/>
                            </svg>
                          </div>
                        ) : null}
                      </div>

                      {activeTab === 'mine' ? (
                        <div className="InspirationCanvas-card-info">
                          <div className="InspirationCanvas-card-meta-row">
                            <h3 className="InspirationCanvas-card-title">{project.name}</h3>
                            <div className="InspirationCanvas-project-menu">
                              <button
                                type="button"
                                className="InspirationCanvas-project-menu-trigger"
                                aria-label={`${project.name}更多操作`}
                                aria-haspopup="menu"
                                aria-expanded={isProjectMenuOpen}
                                title="更多操作"
                                onClick={(event) => {
                                  event.stopPropagation();
                                  setOpenProjectMenuId(isProjectMenuOpen ? null : projectMenuId);
                                }}
                              >
                                <svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor" aria-hidden="true">
                                  <circle cx="5" cy="12" r="1.6" />
                                  <circle cx="12" cy="12" r="1.6" />
                                  <circle cx="19" cy="12" r="1.6" />
                                </svg>
                              </button>
                              {isProjectMenuOpen && (
                                <div className="InspirationCanvas-project-menu-popover" role="menu">
                                  <button type="button" role="menuitem" onClick={(event) => { event.stopPropagation(); handleChangeCoverClick(project); }}>
                                    更换封面
                                  </button>
                                  <button type="button" role="menuitem" onClick={(event) => { event.stopPropagation(); handleToggleVisibility(project); }}>
                                    {project.is_public ? '设为私有' : '设为公开'}
                                  </button>
                                  <button type="button" role="menuitem" onClick={(event) => { event.stopPropagation(); handleEditClick(project); }}>
                                    重命名
                                  </button>
                                  <span className="InspirationCanvas-project-menu-divider" role="separator" />
                                  <button type="button" role="menuitem" className="is-danger" onClick={(event) => { event.stopPropagation(); handleDeleteClick(project); }}>
                                    删除项目
                                  </button>
                                </div>
                              )}
                            </div>
                          </div>
                          <time className="InspirationCanvas-project-date" dateTime={projectDate}>
                            {formatProjectDate(projectDate)}
                          </time>
                          {project.admin_public_disabled && (
                            <span className="InspirationCanvas-card-moderation-status">
                              已由管理员关闭公开
                            </span>
                          )}
                        </div>
                      ) : (
                        <div className="InspirationCanvas-card-info">
                          <h3 className="InspirationCanvas-card-title">{project.name}</h3>
                          {project.admin_public_disabled && (
                            <span className="InspirationCanvas-card-moderation-status">
                              已由管理员关闭公开
                            </span>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          ))}
        </div>}

        {!isInitialProjectLoading && activeLoading && activeProjectCount > 0 && (
          <MasonrySkeletonGrid columns={numColumns} compact={activeTab === 'mine'} />
        )}

        {/* 加载更多触发器 - 哨兵元素 */}
        <div ref={sentinelRef} style={{ height: '20px', width: '100%' }}></div>

        {/* 状态提示 */}
        {activeLoading && !isInitialProjectLoading && (
          <div className="InspirationCanvas-loading">加载中...</div>
        )}
        
        {((activeTab === 'discovery' && !discoveryHasMore && discoveryProjects.length > 0) || 
          (activeTab === 'mine' && !mineHasMore && mineProjects.length > 0)) && (
          <div className="InspirationCanvas-no-more">没有更多了</div>
        )}
        
        {((activeTab === 'discovery' && !discoveryLoading && discoveryProjects.length === 0) || 
          (activeTab === 'mine' && userInfo?.token && !mineLoading && mineProjects.length === 0)) && (
          <div className="InspirationCanvas-empty-message">
            <p>暂无内容</p>
          </div>
        )}
      </main>
      </div>

      <input
        ref={coverInputRef}
        className="InspirationCanvas-visually-hidden"
        type="file"
        accept="image/*"
        aria-label="上传项目封面"
        onChange={handleCoverFileChange}
      />

      {coverProject && (
        <div className="InspirationCanvas-dialog-overlay" onClick={closeCoverPicker}>
          <div
            className="InspirationCanvas-dialog InspirationCanvas-cover-dialog"
            role="dialog"
            aria-modal="true"
            aria-labelledby="InspirationCanvas-cover-dialog-title"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="InspirationCanvas-cover-dialog-header">
              <div>
                <h2 id="InspirationCanvas-cover-dialog-title" className="InspirationCanvas-dialog-title">
                  更换项目封面
                </h2>
                <p className="InspirationCanvas-cover-dialog-subtitle">
                  从“{coverProject.name}”的生成结果中选择一张图片
                </p>
              </div>
              <button
                type="button"
                className="InspirationCanvas-cover-dialog-close"
                aria-label="关闭更换封面"
                title="关闭"
                onClick={closeCoverPicker}
                disabled={coverSaving}
              >
                <svg viewBox="0 0 24 24" width="18" height="18" fill="none" aria-hidden="true">
                  <path d="M6 6l12 12M18 6L6 18" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
                </svg>
              </button>
            </div>

            {coverLoading && (
              <div className="InspirationCanvas-cover-state" role="status">
                <span className="InspirationCanvas-cover-spinner" aria-hidden="true" />
                正在读取项目中的生成结果...
              </div>
            )}

            {!coverLoading && coverError && (
              <div className="InspirationCanvas-cover-state is-error" role="alert">
                <strong>读取失败</strong>
                <span>{coverError}</span>
                <button
                  type="button"
                  className="InspirationCanvas-cover-retry"
                  onClick={() => handleChangeCoverClick(coverProject)}
                  disabled={coverSaving}
                >
                  重新读取
                </button>
              </div>
            )}

            {!coverLoading && !coverError && coverImages.length > 0 && (
              <div className="InspirationCanvas-cover-picker" role="radiogroup" aria-label="项目生成结果">
                {coverImages.map((image, index) => {
                  const isSelected = selectedCoverUrl === image.url;
                  return (
                    <button
                      type="button"
                      role="radio"
                      aria-checked={isSelected}
                      aria-label={`选择生成结果 ${index + 1}`}
                      className={`InspirationCanvas-cover-option${isSelected ? ' is-selected' : ''}`}
                      key={image.id}
                      onClick={() => setSelectedCoverUrl(image.url)}
                    >
                      <span className="InspirationCanvas-cover-option-image">
                        <img src={image.url} alt={`生成结果 ${index + 1}`} />
                        {isSelected && (
                          <span className="InspirationCanvas-cover-option-check" aria-hidden="true">
                            <svg viewBox="0 0 24 24" width="16" height="16" fill="none">
                              <path d="m5 12 4.2 4.2L19 6.5" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" />
                            </svg>
                          </span>
                        )}
                      </span>
                      <span className="InspirationCanvas-cover-option-label">
                        {image.label} · 结果 {image.index}
                      </span>
                    </button>
                  );
                })}
              </div>
            )}

            {!coverLoading && !coverError && coverImages.length === 0 && (
              <div className="InspirationCanvas-cover-state is-empty">
                <svg viewBox="0 0 48 48" width="36" height="36" fill="none" aria-hidden="true">
                  <rect x="6" y="8" width="36" height="32" rx="4" stroke="currentColor" strokeWidth="2" />
                  <circle cx="18" cy="19" r="3" stroke="currentColor" strokeWidth="2" />
                  <path d="m8 35 11-10 8 7 5-5 8 8" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
                <strong>项目中暂无可用的生成结果</strong>
                <span>先在画布中生成图片，完成后即可从这里设置封面。</span>
              </div>
            )}

            <div className="InspirationCanvas-cover-dialog-footer">
              <button
                type="button"
                className="InspirationCanvas-cover-upload-button"
                onClick={() => window.setTimeout(() => coverInputRef.current?.click(), 0)}
                disabled={coverSaving}
              >
                上传本地图片
              </button>
              <div className="InspirationCanvas-dialog-actions">
                <button
                  type="button"
                  className="InspirationCanvas-dialog-button InspirationCanvas-dialog-button-cancel"
                  onClick={closeCoverPicker}
                  disabled={coverSaving}
                >
                  取消
                </button>
                <button
                  type="button"
                  className="InspirationCanvas-dialog-button InspirationCanvas-dialog-button-save"
                  onClick={handleSaveSelectedCover}
                  disabled={!selectedCoverUrl || coverSaving}
                >
                  {coverSaving ? '保存中...' : '设为封面'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* 编辑项目对话框 */}
      {showDialog && (
        <div className="InspirationCanvas-dialog-overlay" onClick={() => setShowDialog(false)}>
          <div 
            className="InspirationCanvas-dialog"
            onClick={(e) => e.stopPropagation()}
          >
            <h2 className="InspirationCanvas-dialog-title">
              编辑项目
            </h2>

            <div className="InspirationCanvas-form">
              <div className="InspirationCanvas-form-group">
                <label className="InspirationCanvas-form-label">项目名称 *</label>
                <input
                  type="text"
                  className="InspirationCanvas-form-input"
                  value={projectForm.name}
                  onChange={(e) => setProjectForm({ ...projectForm, name: e.target.value })}
                  placeholder="输入项目名称"
                  autoFocus
                />
              </div>

              <div className="InspirationCanvas-form-group">
                <label className="InspirationCanvas-form-label">项目描述</label>
                <textarea
                  className="InspirationCanvas-form-textarea"
                  value={projectForm.description}
                  onChange={(e) => setProjectForm({ ...projectForm, description: e.target.value })}
                  placeholder="输入项目描述（可选）"
                  rows={3}
                />
              </div>

              <div className="InspirationCanvas-form-group">
                <label className="InspirationCanvas-form-checkbox">
                  <input
                    type="checkbox"
                    checked={projectForm.is_public}
                    disabled={Boolean(editingProject?.admin_public_disabled)}
                    onChange={(e) => setProjectForm({ ...projectForm, is_public: e.target.checked })}
                  />
                  <span>公开项目（其他用户可以查看）</span>
                </label>
                {editingProject?.admin_public_disabled && (
                  <div className="InspirationCanvas-moderation-notice" role="status">
                    <strong>该画布已由管理员关闭公开</strong>
                    <span>
                      {editingProject.admin_public_disabled_reason || '如需恢复，请联系管理员。'}
                    </span>
                  </div>
                )}
              </div>

              <div className="InspirationCanvas-dialog-actions">
                <button
                  className="InspirationCanvas-dialog-button InspirationCanvas-dialog-button-cancel"
                  onClick={() => setShowDialog(false)}
                >
                  取消
                </button>
                <button
                  className="InspirationCanvas-dialog-button InspirationCanvas-dialog-button-save"
                  onClick={handleSave}
                >
                  保存
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* 删除确认对话框 */}
      {showDeleteDialog && deletingProject && (
        <div className="InspirationCanvas-dialog-overlay" onClick={handleCancelDelete}>
          <div 
            className="InspirationCanvas-dialog InspirationCanvas-delete-dialog"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="InspirationCanvas-delete-icon">
              <svg width="56" height="56" viewBox="0 0 56 56" fill="none">
                <circle cx="28" cy="28" r="28" fill="#FEE2E2"/>
                <path d="M28 20V30M28 34V36" stroke="#DC2626" strokeWidth="3" strokeLinecap="round"/>
              </svg>
            </div>

            <h2 className="InspirationCanvas-dialog-title InspirationCanvas-delete-title">
              确认删除项目
            </h2>

            <p className="InspirationCanvas-delete-message">
              确定要删除项目 <strong>"{deletingProject.name}"</strong> 吗？
            </p>
            
            <p className="InspirationCanvas-delete-warning">
              ⚠️ 此操作无法撤销，项目的所有数据将被永久删除。
            </p>

            {deleteError && (
              <div className="InspirationCanvas-delete-error">
                {deleteError}
              </div>
            )}

            <div className="InspirationCanvas-dialog-actions">
              <button
                className="InspirationCanvas-dialog-button InspirationCanvas-dialog-button-cancel"
                onClick={handleCancelDelete}
                disabled={isDeleting}
              >
                取消
              </button>
              <button
                className="InspirationCanvas-dialog-button InspirationCanvas-dialog-button-delete"
                onClick={handleConfirmDelete}
                disabled={isDeleting}
              >
                {isDeleting ? '删除中...' : '确认删除'}
              </button>
            </div>
          </div>
        </div>
      )}

      {showUpgradeModal && (
        <div className="InspirationCanvas-dialog-overlay InspirationCanvas-upgrade-overlay" onClick={closeUpgradeModal}>
          <div
            className="InspirationCanvas-dialog InspirationCanvas-upgrade-dialog"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="InspirationCanvas-upgrade-icon">
              <svg width="32" height="32" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M12 2L14.39 8.26L21 9.27L16 13.97L17.18 20.54L12 17.27L6.82 20.54L8 13.97L3 9.27L9.61 8.26L12 2Z" fill="currentColor" />
              </svg>
            </div>
            <h2 className="InspirationCanvas-dialog-title InspirationCanvas-upgrade-title">升级会员解锁更多创作空间</h2>
            <p className="InspirationCanvas-upgrade-message">
              {upgradeMessage || defaultUpgradeMessage}
            </p>
            <ul className="InspirationCanvas-upgrade-benefits">
              <li>非会员：最多创建1个项目</li>
              <li>VIP最多创建10个项目</li>
              <li>SVIP项目创建数量无限制</li>
            </ul>
            <div className="InspirationCanvas-dialog-actions InspirationCanvas-upgrade-actions">
              <button
                className="InspirationCanvas-dialog-button InspirationCanvas-dialog-button-cancel"
                onClick={closeUpgradeModal}
              >
                稍后再说
              </button>
              <button
                className="InspirationCanvas-dialog-button InspirationCanvas-upgrade-button"
                onClick={handleGoToMembership}
              >
                去开通会员
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default InspirationCanvas;
